import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, Loader2 } from "lucide-react";
import { toast } from "sonner";
import BrandStripe from "@/components/BrandStripe";
import Logo from "@/components/Logo";
import { Divider } from "@/components/Divider";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const GOOGLE_AUTH_START = "https://auth.emergentagent.com/";

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const redirect = params.get("redirect") || "/portal";

  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back.");
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + redirect;
    window.location.href = `${GOOGLE_AUTH_START}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="page-login">
      <BrandStripe thickness="h-1.5" />
      <main className="flex-1 grid lg:grid-cols-2">
        {/* LEFT — brand narrative */}
        <section className="flex items-center justify-center px-6 sm:px-10 lg:px-16 py-16 relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-50" />
          <div className="relative w-full max-w-xl">
            <Link to="/"><Logo size={52} /></Link>
            <p className="mt-10 text-xs uppercase tracking-[0.35em] font-bold text-[#00A651]">
              Applicant Portal
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-black mt-3 tracking-tight leading-[1.05]">
              Welcome <span className="text-[#00A651]">back</span>.
            </h1>
            <Divider className="my-6" />
            <p className="font-sub italic text-lg sm:text-xl text-gray-700">
              Pick up exactly where you left off — your documents, intake answers, and
              underwriting progress are all saved to your account.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { l: "Encrypted", v: "256-bit", c: "#00A651" },
                { l: "Bank-Level", v: "SOC 2", c: "#E88C2F" },
                { l: "Sessions", v: "7 days", c: "#ED1C24" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 pl-3" style={{ borderColor: s.c }}>
                  <div className="font-heading font-bold text-lg text-black">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RIGHT — form */}
        <section className="bg-black text-white flex items-center justify-center px-6 sm:px-10 lg:px-16 py-16 relative">
          <div className="absolute top-0 left-0 right-0 h-1 flex">
            <span className="flex-1 bg-[#00A651]" />
            <span className="flex-1 bg-[#E88C2F]" />
            <span className="flex-1 bg-[#ED1C24]" />
            <span className="flex-1 bg-[#F26522]" />
          </div>

          <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
            <div className="w-12 h-12 grid place-items-center bg-[#00A651]/15 text-[#00A651] rounded-sm">
              <LogIn size={20} />
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.3em] font-bold text-[#E88C2F]">
              Sign In
            </p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">
              Continue your application
            </h2>
            <p className="font-sub italic text-gray-400 mt-2">
              Use the email you signed up with.
            </p>

            <div className="mt-8 space-y-4">
              <div>
                <label htmlFor="login-email" className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Email</label>
                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="login-email-input"
                  className="mt-1 w-full bg-transparent border-b-2 border-gray-700 focus:border-[#00A651] outline-none py-2 text-white"
                />
              </div>
              <div>
                <label htmlFor="login-password" className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Password</label>
                <input
                  id="login-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="login-password-input"
                  className="mt-1 w-full bg-transparent border-b-2 border-gray-700 focus:border-[#00A651] outline-none py-2 text-white"
                />
              </div>
            </div>

            {error && (
              <p role="alert" data-testid="login-error"
                 className="mt-4 text-sm text-[#ED1C24] font-bold uppercase tracking-wider">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="mt-8 w-full bg-[#00A651] hover:bg-[#008f45] disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm px-6 py-4 rounded-sm transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Signing in…" : "Sign In"}
            </button>

            <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-widest text-gray-500">
              <span className="flex-1 h-px bg-gray-800" />
              or
              <span className="flex-1 h-px bg-gray-800" />
            </div>

            <button
              type="button"
              onClick={googleLogin}
              data-testid="login-google-button"
              className="w-full bg-white text-black font-bold uppercase tracking-wider text-sm px-6 py-4 rounded-sm hover:bg-gray-100 transition-colors flex items-center justify-center gap-3"
            >
              <GoogleGlyph />
              Continue with Google
            </button>

            <p className="mt-8 text-sm text-gray-400">
              No account yet?{" "}
              <Link to={`/signup?redirect=${encodeURIComponent(redirect)}`} className="text-[#00A651] font-bold hover:underline" data-testid="login-to-signup">
                Create one
              </Link>
            </p>
          </form>
        </section>
      </main>
      <BrandStripe thickness="h-1.5" />
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.4-.2-2.1H12v3.9h5.9c-.1 1-.7 2.5-2.1 3.5l-.02.13 3.05 2.36.21.02c1.94-1.79 3.06-4.43 3.06-7.55z"/>
      <path fill="#34A853" d="M12 23c2.76 0 5.08-.9 6.77-2.47l-3.23-2.5c-.87.6-2.03 1.02-3.54 1.02-2.7 0-5-1.78-5.82-4.24l-.12.01-3.17 2.45-.04.11C4.53 20.53 8 23 12 23z"/>
      <path fill="#FBBC05" d="M6.18 14.81a6.9 6.9 0 010-4.62L6.17 10 2.96 7.5l-.11.05a11 11 0 000 8.9l3.33-2.64z"/>
      <path fill="#EA4335" d="M12 5.38c1.92 0 3.21.83 3.95 1.53l2.88-2.81C17.08 2.51 14.76 1.5 12 1.5 8 1.5 4.53 3.97 2.85 7.55l3.33 2.64C7 7.16 9.3 5.38 12 5.38z"/>
    </svg>
  );
}
